import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Topbar from "../../components/Topbar/Topbar";
import Footer from "../../components/Footer/Footer";
import API from "../../API";
import API2 from "../../API2";
import { hotelroom } from "../../assets/images/images";
import "./checkouthotel.css";
import Modal from "react-modal";
import {
  FaUser, FaEnvelope, FaHome, FaPhone, FaMapPin, FaCalendarAlt, FaBed,
  FaUsers, FaChild, FaIdCard, FaCheckCircle, FaInfoCircle, FaShieldAlt,
  FaMoneyBillWave, FaArrowLeft, FaCreditCard, FaExclamationTriangle,
  FaChevronUp, FaChevronDown, FaVenusMars
} from "react-icons/fa";
import { X } from "lucide-react";

import { useTranslation } from 'react-i18next';

// ════════════════════════════════════════════════════════════════════
// Hoisted modal components — defined OUTSIDE the page component so they
// keep a stable identity between renders. Keeping them inside causes
// React to unmount/remount on every keystroke (inputs lose focus).
// ════════════════════════════════════════════════════════════════════

// Hoisted Field — keeping it out of the component body preserves input focus.
function Field({ icon: Icon, label, error, children }) {
  return (
    <div className="ch-field">
      <label className="ch-label">
        <Icon className="ch-label__ico" />
        <span>{label}</span>
      </label>
      {children}
      {error && <span className="ch-err">{error}</span>}
    </div>
  );
}

function PrivacyTermsModal({ isOpen, onClose, onAccept, t }) {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="ch-modal"
      overlayClassName="ch-modal-overlay"
      contentLabel="Privacy and Terms"
      ariaHideApp={false}
    >
      <div className="ch-modal__head">
        <div className="ch-modal__title">
          <FaShieldAlt />
          <h2>{t('checkouthotel.privacy_terms')}</h2>
        </div>
        <button className="ch-modal__close" onClick={onClose} aria-label="close">
          <X size={20} />
        </button>
      </div>
      <div className="ch-modal__body">
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="ch-term-block">
            <span className="ch-term-block__num">{n}</span>
            <div>
              <h3>{t(`checkouthotel.privacy_section_${n}_title`)}</h3>
              <p>{t(`checkouthotel.privacy_section_${n}_content`)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="ch-modal__foot">
        <button className="ch-modal__accept" onClick={onAccept}>
          <FaCheckCircle /> {t('checkouthotel.accept_terms')}
        </button>
      </div>
    </Modal>
  );
}



export default function Checkouthotel() {

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const rate = location.state?.rate;
  const detail = location.state?.detail;
  const realrate = location.state?.realrate;
  const propertyCode = location.state?.propertyCode;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect unauthenticated users to login. Since router state is lost on full reload,
  // send them back to the hotel page (where they can re-select a rate) after login.
  useEffect(() => {
    if (!localStorage.getItem('protoken')) {
      const back = propertyCode ? `/hotel-one/${propertyCode}` : '/hotels';
      const next = encodeURIComponent(back);
      window.location.href = `/login?next=${next}`;
    }
  }, [propertyCode]);

  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  const openPrivacyModal = (e) => {
    e.preventDefault();
    setIsPrivacyModalOpen(true);
  };

  const closePrivacyModal = () => {
    setIsPrivacyModalOpen(false);
  };

  const handlePrivacyAccept = () => {
    setTermsAccepted(true);
    closePrivacyModal();
  };


  const handleDetailsSave = (details) => {
    setPersonDetails(details);
    
  };
  
  const closeDetailsModal = () => setIsDetailsModalOpen(false);



  const handleRoomSelect = (detail, rate) => {
    if (!detail || !rate) {
      console.error("Detail or rate is undefined. Cannot proceed.");
      return;
    }
  
    // Clear the old detail and rate from localStorage
    localStorage.removeItem("selectedRoomDetail");
    localStorage.removeItem("selectedRoomRate");
  
    // Save the new selected room detail and rate to localStorage
    localStorage.setItem("selectedRoomDetail", JSON.stringify(detail));
    localStorage.setItem("selectedRoomRate", JSON.stringify(rate));
  
    setSelectedRoom(detail);
  
    // Reset occupancy to minimum values when room changes
    const initialAdults = 1;
    const initialChildren = 0;
  
    setFormData((prev) => ({
      ...prev,
      roomId: detail.room_id,
      numAdults: initialAdults,
      numChildren: initialChildren,
    }));
  
    // Calculate initial price with minimum occupancy
    const initialPrice = calculatePricePerNight(detail, initialAdults, initialChildren);
    setPricePerNight(initialPrice);
  
    // Update grand total if dates are selected
    if (checkinDate && checkoutDate) {
      const nights = calculateNights(checkinDate, checkoutDate);
      setGrandTotal(initialPrice * nights);
    }
  };


  const calculatePricePerNight = (detail, numAdults, numChildren) => {
    if (!detail) return 0;

    // Coerce a field to a positive number; returns 0 if missing/null/"" or NaN.
    // Critical: "0" is falsy when compared as Number — we treat 0 as "no value"
    // so the fallback chain can move to the next tier.
    const num = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };

    // Stepped fallback: pick the highest-tier price available for this adult count
    const tiers = [
      num(detail.price_1),
      num(detail.price_2) || num(detail.price_1),
      num(detail.price_3) || num(detail.price_2) || num(detail.price_1),
      num(detail.price_4) || num(detail.price_3) || num(detail.price_2) || num(detail.price_1),
      num(detail.price_5) || num(detail.price_4) || num(detail.price_3) || num(detail.price_2) || num(detail.price_1),
    ];

    let basePrice = 0;
    const adults = Number(numAdults) || 1;
    if (adults >= 1 && adults <= 5) {
      basePrice = tiers[adults - 1];
    } else if (adults > 5) {
      const extraAdultPrice = num(detail.price_extra);
      basePrice = tiers[4] + (adults - 5) * extraAdultPrice;
    }

    const childPrice = num(detail.price_child);
    const totalChildPrice = childPrice * (Number(numChildren) || 0);

    return basePrice + totalChildPrice;
  };
  
  
  






  
  









  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("protoken");

  const [checkinDate, setCheckinDate] = useState(null);
  const [checkoutDate, setCheckoutDate] = useState(null);
  const [totalNights, setTotalNights] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [pricePerNight, setPricePerNight] = useState(0);
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Fetch fully booked dates for the selected room type
  useEffect(() => {
    const propertyId = rate?.property_id;
    const roomTypeId = detail?.room?.id;
    console.log("[AVAIL] rate:", rate);
    console.log("[AVAIL] detail:", detail);
    console.log("[AVAIL] propertyId:", propertyId);
    console.log("[AVAIL] roomTypeId:", roomTypeId);
    if (!propertyId || !roomTypeId) {
      console.warn("[AVAIL] Missing propertyId or roomTypeId - skipping");
      return;
    }

    const fetchAvailability = async () => {
      try {
        setLoadingAvailability(true);
        const today = new Date();
        const end = new Date();
        end.setFullYear(end.getFullYear() + 1);
        const url = "ratepricing/room-availability";
        const params = {
          property_id: propertyId,
          room_type_id: roomTypeId,
          start: today.toISOString().split("T")[0],
          end: end.toISOString().split("T")[0],
        };
        console.log("[AVAIL] Fetching:", API2.defaults.baseURL + url, params);
        const resp = await API2.get(url, { params });
        console.log("[AVAIL] Response:", resp.data);
        const dates = (resp.data?.fully_booked_dates || []).map((d) => {
          const [y, m, day] = d.split("-").map(Number);
          return new Date(y, m - 1, day);
        });
        console.log("[AVAIL] Parsed booked dates:", dates);
        setFullyBookedDates(dates);
      } catch (err) {
        console.error("[AVAIL] Error fetching availability:", err);
        console.error("[AVAIL] Error response:", err?.response?.data);
        console.error("[AVAIL] Error status:", err?.response?.status);
      } finally {
        setLoadingAvailability(false);
      }
    };
    fetchAvailability();
  }, [rate?.property_id, detail?.room?.id]);

  const isDateFullyBooked = (date) => {
    if (!date) return false;
    return fullyBookedDates.some(
      (d) =>
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
    );
  };

  const [formData2, setFormData2] = useState({
    numAdults: 0,
    numChildren: 0
  });


  const currentDateprime = new Date();


  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [personDetails, setPersonDetails] = useState([]);
  const [expandedApplicant, setExpandedApplicant] = useState(0);

  const updateApplicant = (index, field, value) => {
    setFormData((prev) => {
      const next = [...(prev.applicants || [])];
      while (next.length <= index) {
        next.push({
          firstname: "",
          lastname: "",
          birthday: null,
          gender: "",
          documentType: "id_card",
          documentNumber: "",
          additionalInfo: "",
        });
      }
      next[index] = { ...next[index], [field]: value };
      return { ...prev, applicants: next };
    });
  };



  const [formData, setFormData] = useState({
    name: "",
    surnom: "",
    email: "",
    address: "",
    zipcode: "",
    phone: "",
    propertyId: rate?.property_id,
    userId: userId,
    roomId: "", // Add roomId to track selected room
    numAdults: 1,
    numChildren: 0,
    applicants: []
  });


  const handleDetailsSave2 = (details) => {
    setPersonDetails(details);
    setFormData((prev) => ({
      ...prev,
      applicants: details,
    }));
    
    closeDetailsModal();
  };

  

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});

  const notifySuccess = () => 
    toast.success(t('checkouthotel.reservation_success'), {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

  const notifyLoginRequired = () => 
    toast.error(t('checkouthotel.login_required'), {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

  const notifyError = (message) => 
    toast.error(`${t('checkouthotel.error')} ${message}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  // Validation functions
  const isValidName = (name) => name.trim().length > 1;
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => /^(05|06|07)\d{8}$/.test(phone);

  // Iterate each night in [start, end) and count only nights that are
  // NOT in fullyBookedDates. This lets users book around gaps of unavailable
  // days (e.g. pick 24–30 while 26/27/28 are booked → charged for 4 nights).
  const calculateNights = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end);   e.setHours(0, 0, 0, 0);
    if (e <= s) return 0;

    const bookedSet = new Set(
      (fullyBookedDates || []).map((d) => {
        const dt = d instanceof Date ? d : new Date(d);
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
      })
    );

    let nights = 0;
    const cursor = new Date(s);
    while (cursor < e) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, "0");
      const dd = String(cursor.getDate()).padStart(2, "0");
      const key = `${y}-${m}-${dd}`;
      if (!bookedSet.has(key)) nights += 1;
      cursor.setDate(cursor.getDate() + 1);
    }
    return nights;
  };

  const calculateGrandTotal = (nights) => {
    if (!rate || !rate.details || nights === 0) return 0;
    return rate.details.reduce((total, detail) => {
      const pricePerNight = parseInt(detail.price_1 || "0", 10);
      return total + pricePerNight * nights;
    }, 0);
  };

  useEffect(() => {
    if (checkinDate && checkoutDate && selectedRoom) {
      const nights = calculateNights(checkinDate, checkoutDate);
      setTotalNights(nights);
      setGrandTotal(pricePerNight * nights);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkinDate, checkoutDate, selectedRoom, pricePerNight, fullyBookedDates]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    let numValue = value;
  
    if (name === 'numAdults' || name === 'numChildren') {
      numValue = parseInt(value, 10) || 0;
  
      const totalApplicants = (name === 'numAdults' ? numValue : formData.numAdults) + (name === 'numChildren' ? numValue : formData.numChildren);
      setFormData((prev) => ({
        ...prev,
        [name]: numValue,
        applicants: Array(totalApplicants).fill().map((_, index) => prev.applicants[index] || {
          firstname: "",
          lastname: "",
          birthday: null,
          gender: "",
          documentType: "id_card",
          documentNumber: "",
          additionalInfo: "",
        }),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  
    if ((name === 'numAdults' || name === 'numChildren') && selectedRoom) {
      const newAdults = name === 'numAdults' ? numValue : formData.numAdults;
      const newChildren = name === 'numChildren' ? numValue : formData.numChildren;
  
      const newPricePerNight = calculatePricePerNight(selectedRoom, newAdults, newChildren);
      setPricePerNight(newPricePerNight);
  
      if (checkinDate && checkoutDate) {
        const nights = calculateNights(checkinDate, checkoutDate);
        setGrandTotal(newPricePerNight * nights);
      }
    }
  };
  
  
  

  const validateForm = () => {
    const newErrors = {};

    // Every adult + child needs a filled applicant card (the inline accordion
    // renders numAdults + numChildren cards).
    const totalApplicants = Number(formData.numAdults || 0) + Number(formData.numChildren || 0);
    const applicants = formData.applicants || [];

    if (totalApplicants > 0) {
      const allFilled = Array.from({ length: totalApplicants }).every((_, i) => {
        const a = applicants[i];
        return a && a.firstname && a.lastname && a.birthday && a.gender
          && a.documentType && a.documentNumber;
      });
      if (!allFilled) {
        newErrors.applicants = "Please complete all applicant details.";
      }
    }
  
    // Other validation checks...
    if (!isValidName(formData.name))
      newErrors.name = t('checkouthotel.invalid_name_error');
  
    if (!isValidName(formData.surnom))
      newErrors.surnom = t('checkouthotel.invalid_name_error');
  
    if (!isValidEmail(formData.email))
      newErrors.email = t('checkouthotel.invalid_email_error');
  
    if (formData.address.trim().length < 5)
      newErrors.address = t('checkouthotel.required_field_error');
  
    if (!isValidPhone(formData.phone))
      newErrors.phone = t('checkouthotel.invalid_phone_error');
  
    if (!checkinDate)
      newErrors.checkinDate = t('checkouthotel.invalid_checkin_error');
  
    if (!checkoutDate)
      newErrors.checkoutDate = t('checkouthotel.invalid_checkout_error');
  
    if (checkinDate && checkoutDate && checkoutDate <= checkinDate)
      newErrors.checkoutDate = t('checkouthotel.invalid_checkout_error');
  
    if (!termsAccepted)
      newErrors.terms = t('checkouthotel.terms_error');
  
    if (!selectedRoom) {
      newErrors.room = t('checkouthotel.room_error');
    } else {
      if (formData.numAdults < 1 || formData.numAdults > selectedRoom.room.max_adults) {
        newErrors.numAdults = t('checkouthotel.adults_error', { maxAdults: selectedRoom.room.max_adults });
      }
      if (formData.numChildren > selectedRoom.room.max_children) {
        newErrors.numChildren = t('checkouthotel.children_error', { maxChildren: selectedRoom.room.max_children });
      }
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


useEffect(() => {
  if (detail && rate) {
    localStorage.setItem("selectedRoomDetail", JSON.stringify(detail));
    localStorage.setItem("selectedRoomRate", JSON.stringify(rate));
  }
 
}, [detail, rate]);

useEffect(() => {
  const savedDetail = JSON.parse(localStorage.getItem("selectedRoomDetail"));
  const savedRate = JSON.parse(localStorage.getItem("selectedRoomRate"));
  if (savedDetail && savedRate) {
    setSelectedRoom(savedDetail);
    setFormData((prev) => ({
      ...prev,
      roomId: savedDetail.room_id,
    }));
        // Calculate the initial price for 1 adult and 0 children
        const initialPrice = calculatePricePerNight(savedDetail, 1, 0);
        setPricePerNight(initialPrice);
  }
}, []);

const handlePayment = async () => {
  if (!token) {
    notifyLoginRequired();
    return;
  }

  // Validate the main form
  if (!validateForm()) {
    return; // Stop if validation fails
  }

  // Ensure every adult + child has a fully filled applicant card
  const totalApplicants = Number(formData.numAdults || 0) + Number(formData.numChildren || 0);
  const applicants = formData.applicants || [];
  const allFilled = Array.from({ length: totalApplicants }).every((_, i) => {
    const a = applicants[i];
    return a && a.firstname && a.lastname && a.birthday && a.gender
      && a.documentType && a.documentNumber;
  });
  if (!allFilled) {
    notifyError("Please complete all applicant details.");
    return;
  }

  // Prepare submission data
  const { roomId, ...submissionData } = {
    ...formData,
    checkinDate: checkinDate?.toISOString().split("T")[0],
    checkoutDate: checkoutDate?.toISOString().split("T")[0],
    totalNights,
    totalPrice: grandTotal,
    numAdults: formData.numAdults,
    numChildren: formData.numChildren,
    roomType:detail.room.id,
    ratestr:realrate,
  };

  try {
    const response = await API.post("/hotelreservations", submissionData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    notifySuccess();
    setTimeout(() => {
      navigate('/');
    }, 3000);
  } catch (error) {
    console.error("Error submitting data:", error);
    notifyError(error.response?.data?.message || "Unexpected error occurred.");
  }
};

  if (!rate) {
    const backHref = propertyCode ? `/hotel-one/${propertyCode}` : '/hotels';
    return (
      <div className="Checkout_hotel">
        <Topbar />
        <Navbar />
        <div className="ch-main">
          <div className="ch-empty">
            <FaExclamationTriangle />
            <h2>{t('checkouthotel.big_title')}</h2>
            <p>Your room selection was lost. Please pick a room again to continue.</p>
            <a href={backHref} className="ch-btn ch-btn--primary" style={{ marginTop: '1rem', display: 'inline-flex' }}>
              <FaArrowLeft /> Back to hotel
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') return "N/A";
    const n = Number(price);
    if (!Number.isFinite(n)) return "N/A";
    return `${n.toLocaleString("fr-FR")} DZD`;
  };

  return (
    <div className="Checkout_hotel">
      <Topbar />
      <Navbar />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* ═══ Hero ═══ */}
      <section className="ch-hero">
        <div className="ch-hero__icon"><FaCreditCard /></div>
        <h1 className="ch-hero__title">{t('checkouthotel.big_title')}</h1>
        <p className="ch-hero__sub">{t('checkouthotel.general_information')}</p>
      </section>

      <main className="ch-main">
        <div className="ch-grid">
          {/* ═══ FORM COLUMN ═══ */}
          <div className="ch-col-form">
            <section className="ch-card">
              <div className="ch-card__head">
                <span className="ch-pill"><span className="ch-pill__num">1</span> Guest</span>
                <h2 className="ch-card__title">{t('checkouthotel.general_information')}</h2>
              </div>

              <div className="ch-form">
                <Field icon={FaUser} label={t('checkouthotel.name_label')} error={errors.name}>
                  <input type="text" className="ch-input" placeholder="Mohamed" name="name" value={formData.name} onChange={handleInputChange} />
                </Field>
                <Field icon={FaUser} label={t('checkouthotel.surname_label')} error={errors.surnom}>
                  <input type="text" className="ch-input" placeholder="El Amine" name="surnom" value={formData.surnom} onChange={handleInputChange} />
                </Field>
                <div className="ch-field ch-field--full">
                  <label className="ch-label">
                    <FaEnvelope className="ch-label__ico" />
                    <span>{t('checkouthotel.email_label')}</span>
                  </label>
                  <input type="email" className="ch-input" placeholder="mohamed.elamine@gmail.com" name="email" value={formData.email} onChange={handleInputChange} />
                  {errors.email && <span className="ch-err">{errors.email}</span>}
                </div>
                <div className="ch-field ch-field--full">
                  <label className="ch-label">
                    <FaHome className="ch-label__ico" />
                    <span>{t('checkouthotel.address_label')}</span>
                  </label>
                  <input type="text" className="ch-input" placeholder="Batna street 300" name="address" value={formData.address} onChange={handleInputChange} />
                  {errors.address && <span className="ch-err">{errors.address}</span>}
                </div>
                <Field icon={FaMapPin} label={t('checkouthotel.zipcode_label')}>
                  <input type="text" className="ch-input" placeholder="5005" name="zipcode" value={formData.zipcode} onChange={handleInputChange} />
                </Field>
                <Field icon={FaPhone} label={t('checkouthotel.phone_label')} error={errors.phone}>
                  <input type="tel" className="ch-input" placeholder="0512345678" name="phone" value={formData.phone} onChange={handleInputChange} />
                </Field>
              </div>
            </section>

            <section className="ch-card">
              <div className="ch-card__head">
                <span className="ch-pill"><span className="ch-pill__num">2</span> Stay</span>
                <h2 className="ch-card__title">{t('checkouthotel.checkin_label')} / {t('checkouthotel.checkout_label')}</h2>
              </div>

              <div className="ch-form">
                <Field icon={FaCalendarAlt} label={t('checkouthotel.checkin_label')} error={errors.checkinDate}>
                  <div className="ch-date">
                    <DatePicker
                      selected={checkinDate}
                      onChange={(date) => setCheckinDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select check-in date"
                      minDate={currentDateprime}
                      excludeDates={fullyBookedDates}
                      dayClassName={(date) => (isDateFullyBooked(date) ? "booked-date" : undefined)}
                    />
                  </div>
                </Field>
                <Field icon={FaCalendarAlt} label={t('checkouthotel.checkout_label')} error={errors.checkoutDate}>
                  <div className="ch-date">
                    <DatePicker
                      selected={checkoutDate}
                      onChange={(date) => setCheckoutDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select check-out date"
                      minDate={checkinDate || currentDateprime}
                      excludeDates={fullyBookedDates}
                      dayClassName={(date) => (isDateFullyBooked(date) ? "booked-date" : undefined)}
                    />
                  </div>
                </Field>

                {selectedRoom && (
                  <>
                    <Field icon={FaUsers} label={t('checkouthotel.adults_label')} error={errors.numAdults}>
                      <select className="ch-select" name="numAdults" value={formData.numAdults} onChange={handleInputChange}>
                        {[...Array(selectedRoom.room.max_adults)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1} {i === 0 ? 'Adult' : 'Adults'}</option>
                        ))}
                      </select>
                    </Field>
                    <Field icon={FaChild} label={t('checkouthotel.children_label')} error={errors.numChildren}>
                      <select className="ch-select" name="numChildren" value={formData.numChildren} onChange={handleInputChange}>
                        {[...Array(selectedRoom.room.max_children + 1)].map((_, i) => (
                          <option key={i} value={i}>{i} {i === 1 ? 'Child' : 'Children'}</option>
                        ))}
                      </select>
                    </Field>
                  </>
                )}
              </div>

              {fullyBookedDates.length > 0 && (
                <div className="ch-banner ch-banner--warn">
                  <FaExclamationTriangle />
                  <p>Some dates are unavailable — all rooms of this type are reserved. Unavailable dates are disabled in the calendar.</p>
                </div>
              )}
              {loadingAvailability && (
                <p className="ch-loading">Checking availability...</p>
              )}
            </section>

            {selectedRoom && (
              <section className="ch-card">
                <div className="ch-card__head">
                  <span className="ch-pill"><span className="ch-pill__num">3</span> Guests</span>
                  <h2 className="ch-card__title">{t('checkouthotel.person_details')}</h2>
                </div>

                {/* Inline accordion — one card per applicant (no modal) */}
                <div className="ch-applicants">
                  {Array.from({ length: Number(formData.numAdults) + Number(formData.numChildren) }).map((_, idx) => {
                    const isChild = idx >= Number(formData.numAdults);
                    const person = formData.applicants[idx] || {
                      firstname: "", lastname: "", birthday: null, gender: "",
                      documentType: "id_card", documentNumber: "", additionalInfo: "",
                    };
                    const isExpanded = expandedApplicant === idx;
                    const isFilled =
                      person.firstname && person.lastname && person.birthday &&
                      person.gender && person.documentNumber;

                    return (
                      <div
                        key={idx}
                        className={`ch-applicant ${isExpanded ? "is-expanded" : ""}`}
                      >
                        <button
                          type="button"
                          className="ch-applicant__head"
                          onClick={() => setExpandedApplicant(isExpanded ? -1 : idx)}
                        >
                          <span className={`ch-applicant__icon ${isChild ? "is-child" : ""}`}>
                            {isChild ? <FaChild /> : <FaUser />}
                          </span>
                          <span className="ch-applicant__label">
                            {isChild ? `Child #${idx - Number(formData.numAdults) + 1}` : `Adult #${idx + 1}`}
                          </span>
                          {person.firstname && person.lastname && (
                            <span className="ch-applicant__preview">
                              {person.firstname} {person.lastname}
                            </span>
                          )}
                          {isFilled && (
                            <span className="ch-applicant__ok" title="Filled">
                              <FaCheckCircle />
                            </span>
                          )}
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>

                        {isExpanded && (
                          <div className="ch-applicant__body">
                            <div className="ch-form">
                              <div className="ch-field">
                                <label className="ch-label">
                                  <FaUser className="ch-label__ico" />
                                  <span>{t('checkouthotel.first_name_label')}</span>
                                </label>
                                <input
                                  type="text"
                                  className="ch-input"
                                  value={person.firstname}
                                  onChange={(e) => updateApplicant(idx, "firstname", e.target.value)}
                                  placeholder="Enter first name"
                                />
                              </div>

                              <div className="ch-field">
                                <label className="ch-label">
                                  <FaUser className="ch-label__ico" />
                                  <span>{t('checkouthotel.last_name_label')}</span>
                                </label>
                                <input
                                  type="text"
                                  className="ch-input"
                                  value={person.lastname}
                                  onChange={(e) => updateApplicant(idx, "lastname", e.target.value)}
                                  placeholder="Enter last name"
                                />
                              </div>

                              <div className="ch-field">
                                <label className="ch-label">
                                  <FaCalendarAlt className="ch-label__ico" />
                                  <span>{t('checkouthotel.birthday_label')}</span>
                                </label>
                                <div className="ch-date">
                                  <DatePicker
                                    selected={person.birthday ? new Date(person.birthday) : null}
                                    onChange={(date) => updateApplicant(idx, "birthday", date)}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="YYYY-MM-DD"
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={80}
                                    scrollableYearDropdown
                                    openToDate={person.birthday ? new Date(person.birthday) : new Date(2000, 0, 1)}
                                    minDate={new Date(1924, 0, 1)}
                                    maxDate={new Date()}
                                  />
                                </div>
                              </div>

                              <div className="ch-field">
                                <label className="ch-label">
                                  <FaVenusMars className="ch-label__ico" />
                                  <span>{t('checkouthotel.gender_label')}</span>
                                </label>
                                <select
                                  className="ch-select"
                                  value={person.gender}
                                  onChange={(e) => updateApplicant(idx, "gender", e.target.value)}
                                >
                                  <option value="" disabled>Select gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                </select>
                              </div>

                              <div className="ch-field">
                                <label className="ch-label">
                                  <FaIdCard className="ch-label__ico" />
                                  <span>{t('checkouthotel.document_type_label')}</span>
                                </label>
                                <select
                                  className="ch-select"
                                  value={person.documentType}
                                  onChange={(e) => updateApplicant(idx, "documentType", e.target.value)}
                                >
                                  <option value="id_card">ID Card</option>
                                  <option value="passport">Passport</option>
                                </select>
                              </div>

                              <div className="ch-field">
                                <label className="ch-label">
                                  <FaIdCard className="ch-label__ico" />
                                  <span>{t('checkouthotel.document_number_label')}</span>
                                </label>
                                <input
                                  type="text"
                                  className="ch-input"
                                  value={person.documentNumber}
                                  onChange={(e) => {
                                    const v = (e.target.value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                    updateApplicant(idx, "documentNumber", v);
                                  }}
                                  placeholder="Enter document number"
                                  maxLength={person.documentType === "passport" ? 9 : 20}
                                />
                              </div>

                              <div className="ch-field ch-field--full">
                                <label className="ch-label">
                                  <FaInfoCircle className="ch-label__ico" />
                                  <span>{t('checkouthotel.additional_info_label')}</span>
                                </label>
                                <textarea
                                  className="ch-textarea"
                                  value={person.additionalInfo || ""}
                                  onChange={(e) => updateApplicant(idx, "additionalInfo", e.target.value)}
                                  placeholder="Enter any additional information"
                                  rows="3"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {errors.applicants && <span className="ch-err">{errors.applicants}</span>}
              </section>
            )}

            <section className="ch-card">
              <label className={`ch-accept ${termsAccepted ? 'ch-accept--on' : ''}`}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                />
                <span className="ch-accept__box" />
                <span className="ch-accept__text">
                  Please check to acknowledge our{" "}
                  <a href="/#" onClick={openPrivacyModal}>{t('checkouthotel.privacy_terms')}</a>
                </span>
              </label>
              {errors.terms && <span className="ch-err">{errors.terms}</span>}

              <PrivacyTermsModal
                isOpen={isPrivacyModalOpen}
                onClose={closePrivacyModal}
                onAccept={handlePrivacyAccept}
                t={t}
              />
            </section>
          </div>

          {/* ═══ SUMMARY COLUMN ═══ */}
          <aside className="ch-col-summary">
            <div className="ch-summary">
              <div className="ch-summary__hero">
                <img src={hotelroom} alt="hotel room" />
                <div className="ch-summary__overlay">
                  <span className="ch-summary__eyebrow">
                    <FaMoneyBillWave /> {t('checkouthotel.billing_summary')}
                  </span>
                </div>
              </div>

              <div className="ch-summary__body">
                {selectedRoom && (
                  <>
                    <div className="ch-summary__row">
                      <div className="ch-summary__label">
                        <FaBed />
                        <div>
                          <p className="ch-summary__name">{selectedRoom.room.code}</p>
                          <p className="ch-summary__hint">{selectedRoom.room.description}</p>
                        </div>
                      </div>
                      <p className="ch-summary__value">{formatPrice(pricePerNight)}<small>/{t('checkouthotel.per_night').replace(/^\s*\//, '')}</small></p>
                    </div>
                    <div className="ch-summary__row">
                      <div className="ch-summary__label">
                        <FaUsers />
                        <p>{t('checkouthotel.occupancy')}</p>
                      </div>
                      <p className="ch-summary__value-sm">
                        {formData.numAdults} {t('checkouthotel.adults')} · {formData.numChildren} {t('checkouthotel.children')}
                      </p>
                    </div>
                  </>
                )}

                <div className="ch-summary__row">
                  <div className="ch-summary__label">
                    <FaCalendarAlt />
                    <p>{t('checkouthotel.total_nights')}</p>
                  </div>
                  <p className="ch-summary__value-sm">
                    {totalNights} {t('checkouthotel.nights')}
                  </p>
                </div>

                <div className="ch-summary__divider" />

                <div className="ch-summary__total">
                  <span>{t('checkouthotel.grand_total')}</span>
                  <strong>{formatPrice(grandTotal)}</strong>
                </div>

                <button className="ch-btn ch-btn--primary ch-btn--block" onClick={handlePayment}>
                  <FaCreditCard /> {t('checkouthotel.payment_button')}
                </button>
                <button className="ch-btn ch-btn--ghost ch-btn--block" onClick={() => window.history.back()}>
                  <FaArrowLeft /> {t('checkouthotel.back_button')}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
